import React from 'react';
import { Check, Loader2, X } from 'lucide-react';
import type { PublisherConfig, SelfServiceSubmissionRow } from '../../types/selfService';
import { apiClient } from '../../lib/apiClient';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';
import { formatMoneySmart } from './shared/format';
import type { CancelCopyVariant } from './campaignSharedHelpers';

interface CancelCampaignModalProps {
  submission: SelfServiceSubmissionRow;
  cfg: PublisherConfig;
  variant: CancelCopyVariant;
  onClose: () => void;
  onSuccess: (updated: { id: string; status: 'canceled'; canceled_at: string }) => void;
}

interface CancelCampaignPanelProps {
  submission: SelfServiceSubmissionRow;
  cfg: PublisherConfig;
  variant: CancelCopyVariant;
  onClose: () => void;
  onSuccess: (updated: { id: string; status: 'canceled'; canceled_at: string }) => void;
  // CTV-440: when rendered inside EditCampaignModal we drop the close (X)
  // button — the parent modal supplies its own. Standalone use keeps the
  // existing close affordance.
  showCloseButton?: boolean;
}

interface CopyForVariant {
  heading: string;
  intro: string;
  detailLabel: string;
  detail: React.ReactNode;
  confirmLabel: string;
  tone: 'accent' | 'warn';
}

function successCopyFor(
  variant: CancelCopyVariant,
  amountCents: number,
): { heading: string; body: React.ReactNode } {
  switch (variant) {
    case 'pending_payment':
      return {
        heading: 'Campaign canceled',
        body: 'No charge will be made to your card.',
      };
    case 'payment_authorized':
      return {
        heading: 'Campaign canceled',
        body: 'The hold on your card will be released within a few business days. No charge was made.',
      };
    case 'approved_pre_flight':
      return {
        heading: 'Refund issued',
        body: (
          <>
            A refund of{' '}
            <strong className="text-slate-900">${formatMoneySmart(amountCents / 100)}</strong>{' '}
            will arrive at your original payment method within 5–10 business days.
          </>
        ),
      };
    case 'approved_live_no_refund':
      return {
        heading: 'Campaign stopped',
        body: 'Delivery has been halted. No refund will be issued per the upfront payment terms.',
      };
    case 'approved_live_monthly':
      return {
        heading: 'Campaign stopped',
        body: 'Delivery has been halted. Future installments have been canceled. Any current-month payment already due remains due, and paid months are never refunded.',
      };
  }
}

function copyFor(variant: CancelCopyVariant): CopyForVariant {
  switch (variant) {
    case 'pending_payment':
      return {
        heading: 'Cancel this campaign?',
        intro: "You haven't been charged yet.",
        detailLabel: 'What happens next',
        detail: 'We release the pending payment intent. No charge is made.',
        confirmLabel: 'Cancel campaign',
        tone: 'warn',
      };
    case 'payment_authorized':
      return {
        heading: 'Cancel this campaign?',
        intro: 'No charge will be made.',
        detailLabel: 'What happens next',
        detail: 'The payment authorization is released and the hold is removed from your card within a few business days.',
        confirmLabel: 'Cancel campaign',
        tone: 'warn',
      };
    case 'approved_pre_flight':
      return {
        heading: 'Cancel and refund?',
        intro: "Your campaign hasn't started running.",
        detailLabel: 'What happens next',
        detail: "You'll receive a full refund to your original payment method within 5–10 business days. The campaign won't run.",
        confirmLabel: 'Cancel and refund',
        tone: 'warn',
      };
    case 'approved_live_no_refund':
      return {
        heading: 'Stop this campaign now?',
        intro: 'This campaign is already running.',
        detailLabel: 'No refund will be issued',
        detail: 'Delivery stops immediately. You agreed to pay upfront when you submitted, so no refund is issued for this campaign.',
        confirmLabel: 'Stop without refund',
        tone: 'warn',
      };
    case 'approved_live_monthly':
      return {
        heading: 'Stop this campaign now?',
        intro: 'This month-by-month campaign can be stopped before the final billing month begins.',
        detailLabel: 'Future payments canceled',
        detail: 'Delivery stops immediately. We cancel future installments, but any current-month payment already due remains due and paid months are never refunded.',
        confirmLabel: 'Stop campaign',
        tone: 'warn',
      };
  }
}

/**
 * CTV-440: overlay-less form content. Used directly by the Cancel tab inside
 * EditCampaignModal (which supplies its own chrome) and via the thin
 * CancelCampaignModal wrapper below for standalone "Cancel" pill use on
 * archived campaigns.
 */
export function CancelCampaignPanel({
  submission,
  cfg,
  variant,
  onClose,
  onSuccess,
  showCloseButton = true,
}: CancelCampaignPanelProps) {
  const accent = cfg.accentColor || cfg.primaryColor;
  const copy = copyFor(variant);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [done, setDone] = React.useState(false);
  const success = done ? successCopyFor(variant, submission.amount_cents ?? 0) : null;

  // CTV-440: type-to-confirm guard. Buyer must type CANCEL before the
  // destructive button enables. (Previously required the campaign name, but
  // auto-generated default names use an em dash separator the buyer can't type
  // on a normal keyboard — `${advertiser} — ${date}`, submissions.service.ts —
  // which made the gate impossible to satisfy for every default-named campaign.)
  const CONFIRM_WORD = 'CANCEL';
  const [typed, setTyped] = React.useState('');
  const confirmReady = typed.trim().toUpperCase() === CONFIRM_WORD;

  const onConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await apiClient.post<{
        id: string;
        status: 'canceled';
        canceled_at: string;
      }>(`/submissions/${submission.id}/cancel`, {});
      analytics.track(EVENTS.CAMPAIGN_CANCELED, {
        station_slug: cfg.slug,
        submission_id: submission.id,
        variant,
      });
      onSuccess(result);
      setBusy(false);
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to cancel campaign');
      setBusy(false);
    }
  };

  // Tone-aware accents. `warn` uses a red/amber palette so the "no refund"
  // path reads as destructive at a glance; `accent` blends into the
  // publisher's brand color for the routine cancellation paths.
  const railColor = copy.tone === 'warn' ? '#dc2626' : accent;
  const labelColor = copy.tone === 'warn' ? '#b91c1c' : accent;
  const tintBg = copy.tone === 'warn'
    ? 'linear-gradient(180deg, #fef2f2 0%, #fee2e2 100%)'
    : `${accent}0d`;
  const tintRing = copy.tone === 'warn' ? 'rgba(220, 38, 38, 0.18)' : `${accent}26`;

  return success ? (
    <>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <span
            className="inline-flex items-center justify-center w-7 h-7 rounded-full"
            style={{
              background: 'linear-gradient(180deg, #d1fae5 0%, #a7f3d0 100%)',
              boxShadow: '0 0 0 1px rgba(16, 185, 129, 0.25), 0 6px 14px -6px rgba(16, 185, 129, 0.45)',
            }}
          >
            <Check className="w-4 h-4 text-emerald-700" />
          </span>
          <h3 className="text-[15px] font-semibold tracking-tight text-slate-900 leading-tight">
            {success.heading}
          </h3>
        </div>
        {showCloseButton && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 -mr-1 -mt-1 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div
        className="relative rounded-xl px-4 py-3 mt-1"
        style={{
          background: 'linear-gradient(180deg, #ecfdf5 0%, #d1fae5 100%)',
          border: '1px solid rgba(16, 185, 129, 0.18)',
        }}
      >
        <span
          aria-hidden
          className="absolute left-0 top-3 bottom-3 w-[2px] rounded-r"
          style={{ background: '#10b981' }}
        />
        <p className="text-[12.5px] text-slate-700 leading-relaxed pl-2">
          {success.body}
        </p>
      </div>

      <div className="flex items-center justify-end mt-5">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center justify-center px-4 py-1.5 rounded-full text-[12px] font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98]"
          style={{
            background: 'linear-gradient(180deg, #10b981, #047857)',
            boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 14px -8px #047857',
          }}
        >
          Done
        </button>
      </div>
    </>
  ) : (
    <>
      <div className="flex items-start justify-between mb-2.5">
        <div>
          <h3 className="text-[15px] font-semibold tracking-tight text-slate-900 leading-tight">
            {copy.heading}
          </h3>
          <p className="text-[12px] text-slate-500 mt-1">{copy.intro}</p>
        </div>
        {showCloseButton && (
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="p-1 -mr-1 -mt-1 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div
        className="relative rounded-xl px-4 py-3 mt-1"
        style={{ background: tintBg, border: `1px solid ${tintRing}` }}
      >
        <span
          aria-hidden
          className="absolute left-0 top-3 bottom-3 w-[2px] rounded-r"
          style={{ background: railColor }}
        />
        <div
          className="text-[10px] font-semibold uppercase tracking-[0.1em] pl-2"
          style={{ color: labelColor }}
        >
          {copy.detailLabel}
        </div>
        <p className="text-[12.5px] text-slate-700 leading-relaxed mt-1 pl-2">
          {copy.detail}
        </p>
      </div>

      <div className="mt-4">
        <label className="block text-[11px] text-slate-600 mb-1.5">
          Type <span className="font-mono text-slate-900">{CONFIRM_WORD}</span> to confirm.
        </label>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          disabled={busy}
          autoComplete="off"
          spellCheck={false}
          placeholder={CONFIRM_WORD}
          className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-[13px] font-mono text-slate-900 placeholder-slate-300 focus:outline-none focus:border-rose-400 focus:ring-1 focus:ring-rose-200 disabled:opacity-50"
        />
      </div>

      {error && (
        <p className="text-[12px] text-red-600 mt-3 leading-relaxed">
          {error}
        </p>
      )}

      <div className="flex items-center justify-end gap-2 mt-5">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="px-3.5 py-1.5 rounded-full text-[12px] font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors disabled:opacity-50"
        >
          Keep campaign
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy || !confirmReady}
          className="inline-flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-full text-[12px] font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            background: 'linear-gradient(180deg, #ef4444, #b91c1c)',
            boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 14px -8px #991b1b',
          }}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          {copy.confirmLabel}
        </button>
      </div>
    </>
  );
}

/**
 * Standalone overlay-and-dialog wrapper. Still used by CancelButton on
 * archived campaigns (where there is no parent modal). Inside
 * EditCampaignModal the Cancel tab renders CancelCampaignPanel directly.
 */
export function CancelCampaignModal({
  submission,
  cfg,
  variant,
  onClose,
  onSuccess,
}: CancelCampaignModalProps) {
  const accent = cfg.accentColor || cfg.primaryColor;
  const copy = copyFor(variant);
  const railColor = copy.tone === 'warn' ? '#dc2626' : accent;
  return (
    <>
      <div
        className="fixed inset-0 bg-slate-900/45 backdrop-blur-[2px] z-50"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
      />
      <div
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-[420px] bg-white rounded-2xl border border-slate-200/80 overflow-hidden"
        style={{ boxShadow: '0 24px 48px -16px rgba(15,23,42,0.28), 0 2px 4px -1px rgba(15,23,42,0.05)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="h-[3px]"
          style={{ background: `linear-gradient(90deg, transparent, ${railColor}, transparent)` }}
        />
        <div className="p-5">
          <CancelCampaignPanel
            submission={submission}
            cfg={cfg}
            variant={variant}
            onClose={onClose}
            onSuccess={onSuccess}
          />
        </div>
      </div>
    </>
  );
}
